import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { EASE } from "@/components/landing/Reveal";

const LAYERS = [
  { id: "research", name: "RESEARCH", status: "SYNCHRONIZING SOURCES" },
  { id: "script", name: "SCRIPT", status: "DRAFTING NARRATIVE" },
  { id: "storyboard", name: "STORYBOARD", status: "COMPOSING SCENES" },
  { id: "narration", name: "NARRATION", status: "RENDERING VOICE" },
  { id: "visuals", name: "VISUALS", status: "GENERATING FRAMES" },
  { id: "documentary", name: "DOCUMENTARY", status: "FINAL ASSEMBLY" },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden whitespace-nowrap pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const [active, setActive] = useState(0);
  const sectionRef = useRef(null);
  const glowRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setActive((i) => (i + 1) % LAYERS.length), 1700);
    return () => clearInterval(t);
  }, []);

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    glowRef.current?.style.setProperty(
      "background",
      `radial-gradient(560px circle at ${e.clientX - rect.left}px ${e.clientY - rect.top}px, rgba(212,175,55,0.055), transparent 70%)`
    );
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-20"
      data-testid="hero-section"
    >
      <div className="bg-blueprint absolute inset-0" aria-hidden="true" />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% -10%, rgba(212,175,55,0.09) 0%, rgba(9,9,9,0) 62%)" }}
        aria-hidden="true"
      />
      <div ref={glowRef} className="pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-[1.12fr_0.88fr] lg:gap-8 lg:px-8">
        <motion.div style={{ y: yText, opacity: fade }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-8 flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#D4AF37]">
              pitsora — Coming Soon
            </span>
          </motion.div>

          <h1
            className="font-display text-[clamp(2.5rem,6.5vw,4.6rem)] font-extrabold leading-[1.02] tracking-tight"
            data-testid="hero-headline"
          >
            <MaskedLine delay={0.35}>From Research</MaskedLine>
            <MaskedLine delay={0.5}>
              to <span className="text-[#D4AF37]">Documentary</span>.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#9A9A9A] sm:text-lg"
            data-testid="hero-tagline"
          >
            pitsora is building an AI-assisted production studio that transforms research into
            scripts, storyboards, narration, visuals, and finished documentaries.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                scrollToId("waitlist");
              }}
              className="group flex items-center gap-2.5 bg-[#D4AF37] px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#090909] transition-colors duration-300 hover:bg-[#E5A93C]"
              data-testid="hero-cta-waitlist"
            >
              Join the Waitlist
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                scrollToId("how-it-works");
              }}
              className="border border-white/15 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#F5F5F5] transition-colors duration-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
              data-testid="hero-cta-how-it-works"
            >
              See How It Works
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-12 flex items-center gap-3"
            data-testid="hero-status"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#D4AF37] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#D4AF37]" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#666666]">
              pitsora is currently in development
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: yVisual, opacity: fade }}
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.7, ease: EASE }}
          data-testid="hero-timeline-container"
        >
          <div className="relative overflow-hidden border border-white/10 bg-[#111111]/85 shadow-[0_40px_120px_rgba(0,0,0,0.6)] backdrop-blur-md">
            <div
              className="animate-scan absolute left-0 h-px w-full bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent"
              aria-hidden="true"
            />

            <div className="flex items-center justify-between border-b border-white/5 px-5 py-3.5">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-white/15" />
                <span className="h-2 w-2 rounded-full bg-white/15" />
                <span className="h-2 w-2 rounded-full bg-[#D4AF37]/60" />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">
                PITSORA://PRODUCTION_PIPELINE
              </span>
              <span className="animate-blink font-mono text-[10px] tracking-[0.2em] text-[#D4AF37]">
                REC
              </span>
            </div>

            <div className="relative px-5 py-6 sm:px-7">
              <div
                className="absolute bottom-8 left-[38px] top-8 w-px bg-white/8 sm:left-[46px]"
                aria-hidden="true"
              />
              <motion.div
                className="absolute left-[38px] top-8 w-px origin-top bg-[#D4AF37]/70 sm:left-[46px]"
                animate={{ height: `${(active / (LAYERS.length - 1)) * 100}%`, maxHeight: "calc(100% - 64px)" }}
                transition={{ duration: 0.9, ease: EASE }}
                aria-hidden="true"
              />

              <div className="space-y-1.5">
                {LAYERS.map((layer, i) => {
                  const isActive = i === active;
                  const isDone = i < active;
                  return (
                    <div
                      key={layer.id}
                      className={`relative flex items-center gap-4 px-2 py-2.5 transition-colors duration-500 sm:gap-5 ${
                        isActive ? "bg-white/[0.03]" : ""
                      }`}
                      data-testid={`hero-layer-${layer.id}`}
                    >
                      <span
                        className={`relative z-10 h-2 w-2 shrink-0 rounded-full transition-all duration-500 ${
                          isActive
                            ? "bg-[#D4AF37] shadow-[0_0_14px_rgba(212,175,55,0.7)]"
                            : isDone
                              ? "bg-[#D4AF37]/50"
                              : "bg-white/15"
                        }`}
                      />
                      <span
                        className={`w-32 font-mono text-xs tracking-[0.2em] transition-colors duration-500 sm:w-40 sm:text-sm ${
                          isActive ? "text-[#F5F5F5]" : isDone ? "text-[#9A9A9A]" : "text-[#555555]"
                        }`}
                      >
                        {layer.name}
                      </span>
                      <span
                        className={`ml-auto hidden font-mono text-[10px] tracking-[0.18em] transition-opacity duration-500 sm:block ${
                          isActive ? "text-[#D4AF37] opacity-100" : "opacity-0"
                        } hidden whitespace-nowrap font-mono text-[10px] tracking-[0.18em] transition-opacity duration-500 sm:block`}
                      >
                        {layer.status}
                      </span>
                      <span
                        className={`font-mono text-[10px] transition-colors duration-500 ${
                          isActive ? "text-[#D4AF37]" : "text-[#444444]"
                        }`}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative border-t border-white/5 px-5 py-4 sm:px-7">
              <div className="relative flex h-8 items-end gap-[3px]">
                {Array.from({ length: 42 }).map((_, i) => (
                  <span
                    key={i}
                    className={`w-full ${i % 6 === 0 ? "h-6 bg-white/20" : "h-3 bg-white/8"}`}
                  />
                ))}
                <span
                  className="animate-playhead absolute top-0 h-full w-px bg-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.8)]"
                  aria-hidden="true"
                />
              </div>
              <div className="mt-2 flex justify-between font-mono text-[9px] tracking-[0.2em] text-[#555555]">
                <span>00:00:00:00</span>
                <span>TIMELINE — ASSEMBLING</span>
                <span>01:32:47:12</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 lg:flex"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#555555]">Scroll</span>
        <div className="h-10 w-px overflow-hidden bg-white/10">
          <motion.div
            className="h-4 w-px bg-[#D4AF37]"
            animate={{ y: [-16, 40] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}
